<script setup>
import { computed, ref, watch, useSlots, useTemplateRef } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import PreviewPicker from './PreviewPicker.vue';

const props = defineProps({
  caretPosition: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  items: {
    type: Array,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyLabel: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  hint: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select', 'close', 'removeTrigger']);

const search = defineModel('search', { type: String, default: '' });

const MIN_HEIGHT = 320;
const MAX_HEIGHT = 480;
const MAX_WIDTH = 640;
const LIST_ONLY_MAX_WIDTH = 420;
const VIEWPORT_MARGIN = 32;
const SIDE_PREVIEW_MIN_WIDTH = 400;
const STACKED_PREVIEW_MIN_HEIGHT = 280;

const slots = useSlots();

const hasPreview = computed(() => Boolean(slots.preview));

const pickerRef = useTemplateRef('pickerRef');
const selectedIndex = ref(0);

const { width: windowWidth, height: windowHeight } = useWindowSize();

const items = computed(() => props.items);

const width = computed(() => {
  const available = windowWidth.value - VIEWPORT_MARGIN * 2;
  const cap = hasPreview.value ? MAX_WIDTH : LIST_ONLY_MAX_WIDTH;
  return Math.min(cap, available);
});

const height = computed(() =>
  Math.max(
    MIN_HEIGHT,
    Math.min(MAX_HEIGHT, windowHeight.value - VIEWPORT_MARGIN * 2)
  )
);

const previewLayout = computed(() => {
  if (!hasPreview.value) return 'none';
  if (width.value >= SIDE_PREVIEW_MIN_WIDTH) return 'side';
  if (height.value >= STACKED_PREVIEW_MIN_HEIGHT) return 'stacked';
  return 'none';
});

const pickerStyle = computed(() => ({
  width: `${width.value}px`,
  height: `${height.value}px`,
}));

const adjustScroll = () => pickerRef.value?.scrollSelectedIntoView();

const onSelect = () => {
  const item = items.value[selectedIndex.value];
  if (item) emit('select', item);
};

const { moveSelectionUp, moveSelectionDown } = useKeyboardNavigableList({
  items,
  onSelect,
  adjustScroll,
  selectedIndex,
});

const withPicker = action => ({
  action: event => {
    event.preventDefault();
    action();
  },
  allowOnFocusedInput: true,
});

useKeyboardEvents({
  Tab: withPicker(moveSelectionDown),
  'Shift+Tab': withPicker(moveSelectionUp),
  Escape: withPicker(() => emit('close')),
  Backspace: {
    action: event => {
      if (search.value) return;
      event.preventDefault();
      emit('removeTrigger');
    },
    allowOnFocusedInput: true,
  },
});

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

watch(items, () => {
  selectedIndex.value = 0;
  adjustScroll();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center p-8 bg-n-alpha-black1"
      @mousedown.self="emit('close')"
    >
      <PreviewPicker
        ref="pickerRef"
        v-model:selected-index="selectedIndex"
        v-model:search="search"
        v-on-click-outside="() => emit('close')"
        :items="items"
        :search-placeholder="searchPlaceholder"
        :empty-label="emptyLabel"
        :is-loading="isLoading"
        :hint="hint"
        :preview-layout="previewLayout"
        data-popover-content
        :style="pickerStyle"
        @select="onListItemSelection"
      >
        <template v-if="$slots.leading" #leading="slotProps">
          <slot name="leading" v-bind="slotProps" />
        </template>
        <template v-if="$slots.filters" #filters>
          <slot name="filters" />
        </template>
        <template v-if="hasPreview" #preview="slotProps">
          <slot name="preview" v-bind="slotProps" />
        </template>
      </PreviewPicker>
    </div>
  </TeleportWithDirection>
</template>
